// frontend/src/utils/featuredContest.ts
//
// ── Featured Contest priority logic ─────────────────────────────────────
// Surfaces the single highest-priority contest. Used by the Lobby (hero
// "BUILD YOUR LINEUP" button + "Featured" section) and by the /bar/<ref>
// smart-link resolver (bar QR codes route to whatever this returns).
// Priority:
//   1. Active EPL weekend contest (still buildable)
//   2. Active La Liga weekend contest (still buildable)
//   3. Next upcoming EPL weekend contest
//   4. Next upcoming La Liga weekend contest
//   5. Next upcoming EPL daily contest
//   6. Next upcoming La Liga daily contest
// (MLS tiers removed 2026-07 — competition sunset; La Liga added 2026-07-17.)
// `upcoming` arrays arrive pre-sorted ASC by start_time, so .find() returns
// the soonest match. Active contests are filtered for buildability so we never
// surface a locked contest as a build-your-lineup target.

import type { ApiDfsContest } from '@/api/dfs';

export const EPL_COMPETITION_ID = 1;
export const LALIGA_COMPETITION_ID = 4;

export type FeaturedTier =
  | 'active_epl_weekend'
  | 'active_laliga_weekend'
  | 'upcoming_epl_weekend'
  | 'upcoming_laliga_weekend'
  | 'upcoming_epl_daily'
  | 'upcoming_laliga_daily';

// EXACTLY this one competition. A membership test alone would let the
// combined EPL+La Liga weekend slate match the EPL tier and steal the hero -
// and this resolver is also the bar-QR smart-link target, which must land on
// the PURE EPL weekend contest (owner GTM rule). The combined slate lists
// normally in the lobby; it just never gets featured over the flagship.
const hasCompetition = (c: ApiDfsContest, compId: number): boolean =>
  (c.competitions?.length ?? 0) === 1 && (c.competitions?.[0]?.id === compId);

const isContestBuildable = (c: ApiDfsContest): boolean =>
  !c.last_match_start_time || new Date(c.last_match_start_time) > new Date();

export function getFeaturedContest(
  active: ApiDfsContest[],
  upcoming: ApiDfsContest[]
): { contest: ApiDfsContest; tier: FeaturedTier } | null {
  const activeEplWeekend = active.find(
    c => c.is_weekend_contest && hasCompetition(c, EPL_COMPETITION_ID) && isContestBuildable(c)
  );
  if (activeEplWeekend) return { contest: activeEplWeekend, tier: 'active_epl_weekend' };

  const activeLaligaWeekend = active.find(
    c => c.is_weekend_contest && hasCompetition(c, LALIGA_COMPETITION_ID) && isContestBuildable(c)
  );
  if (activeLaligaWeekend) return { contest: activeLaligaWeekend, tier: 'active_laliga_weekend' };

  const upcomingEplWeekend = upcoming.find(
    c => c.is_weekend_contest && hasCompetition(c, EPL_COMPETITION_ID)
  );
  if (upcomingEplWeekend) return { contest: upcomingEplWeekend, tier: 'upcoming_epl_weekend' };

  const upcomingLaligaWeekend = upcoming.find(
    c => c.is_weekend_contest && hasCompetition(c, LALIGA_COMPETITION_ID)
  );
  if (upcomingLaligaWeekend) return { contest: upcomingLaligaWeekend, tier: 'upcoming_laliga_weekend' };

  const upcomingEplDaily = upcoming.find(
    c => !c.is_weekend_contest && hasCompetition(c, EPL_COMPETITION_ID)
  );
  if (upcomingEplDaily) return { contest: upcomingEplDaily, tier: 'upcoming_epl_daily' };

  const upcomingLaligaDaily = upcoming.find(
    c => !c.is_weekend_contest && hasCompetition(c, LALIGA_COMPETITION_ID)
  );
  if (upcomingLaligaDaily) return { contest: upcomingLaligaDaily, tier: 'upcoming_laliga_daily' };

  return null;
}
